import { SegmentedControl } from "@/components/ui/segmented-control";
import { Box, Button, HStack, IconButton, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { RegionOption } from "./RegionOption";
import { MdClose } from "react-icons/md";
import { t } from "i18next";

export interface GameOptionsProps {
  onStartGame: (
    difficulty: "easy" | "medium" | "hard",
    region: "usa" | "eur" | "world"
  ) => void;
  onClose: () => void;
  regions: {
    name: string;
    emoji: string;
    code: string;
  }[];
  defaults: {
    region: "usa" | "eur" | "world";
    difficulty: "easy" | "medium" | "hard";
  };
}

export const GameOptions = ({
  onStartGame,
  onClose,
  regions,
  defaults,
}: GameOptionsProps) => {
  const [selectedDifficulty, setSelectedDifficulty] = useState<
    "easy" | "medium" | "hard"
  >("easy");
  const [difficultyDescription, setDifficultyDescription] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<
    "usa" | "eur" | "world" | null
  >(null);

  useEffect(() => {
    switch (selectedDifficulty) {
      case "easy":
        setDifficultyDescription(t("gameOptions.difficulty.easy.description"));
        break;
      case "medium":
        setDifficultyDescription(
          t("gameOptions.difficulty.medium.description")
        );
        break;
      case "hard":
        setDifficultyDescription(t("gameOptions.difficulty.hard.description"));
        break;
    }
  }, [selectedDifficulty]);

  useEffect(() => {
    if (selectedRegion === null && regions.length > 0) {
      setSelectedRegion(defaults.region || regions[0].code);
    }
  }, [regions]);

  const difficultyOptions = [
    t("gameOptions.difficulty.easy.name"),
    t("gameOptions.difficulty.medium.name"),
    t("gameOptions.difficulty.hard.name"),
  ];

  const difficultyMapping = {
    easy: t("gameOptions.difficulty.easy.name"),
    medium: t("gameOptions.difficulty.medium.name"),
    hard: t("gameOptions.difficulty.hard.name"),
  };

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        size="lg"
        position="absolute"
        top={0}
        left={0}
        m={2}
        variant="ghost"
        color="white"
        _hover={{ background: "whiteAlpha.300" }}
      >
        <MdClose />
      </IconButton>

      <Box
        style={{ color: "white", backgroundColor: "transparent" }}
        w={{ base: "100%", md: "40%" }}
        mt={{ base: 20, md: 0 }}
      >
        <Box display="flex" justifyContent="center">
          <SegmentedControl
            items={difficultyOptions}
            defaultValue={
              difficultyMapping[defaults.difficulty] ||
              difficultyOptions[
                regions.length > 0 && regions[0].code === "eur" ? 1 : 0 // Hack to select medium difficulty by default for Eur
              ]
            }
            onValueChange={(value) => {
              const index = difficultyOptions.indexOf(value.value);
              setSelectedDifficulty(
                ["easy", "medium", "hard"][index] as "easy" | "medium" | "hard"
              );
            }}
            m={3}
            ml={0}
            size={{ base: "md", md: "lg" }}
            background="transparent"
            color="white"
            border="1px solid white"
          />
        </Box>
        <Box display="flex" justifyContent="center">
          <Text
            fontFamily="sans-serif"
            fontSize="sm"
            maxW="300px"
            color="whiteAlpha.800"
          >
            {difficultyDescription}
          </Text>
        </Box>
        <HStack gap={{ base: 4, md: 10 }} mt={10} justifyContent="center">
          {regions.map((region) => (
            <RegionOption
              key={region.code}
              title={t(`gameOptions.regions.${region.code}`)}
              icon={region.emoji}
              selected={selectedRegion === region.code}
              onClick={() =>
                setSelectedRegion(region.code as "usa" | "eur" | "world")
              }
            />
          ))}
        </HStack>
        <Button
          background="green.600"
          size="xl"
          onClick={() => {
            onStartGame(
              selectedDifficulty as "easy" | "medium" | "hard",
              selectedRegion as "usa" | "eur" | "world"
            );
          }}
          w={"100%"}
          h="70px"
          borderRadius="10px"
          fontSize="2xl"
          mt={10}
          color="white"
          _hover={{ background: "green.700" }}
        >
          {t("gameOptions.startGame")}
        </Button>
      </Box>
    </Box>
  );
};
